import React from "react";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper.scss";
import stylesPc from "./VideoSlideGroup.module.css";
import stylesSp from "./VideoSlideGroup_SP.module.css";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import "./navigationButton.css";
import LinearProgress from "@material-ui/core/LinearProgress";
import Modal from "@material-ui/core/Modal";
import PuiTooltip from "../../../components/elements/PuiTooltip ";
import { createStyles, makeStyles } from "@material-ui/core";
import SlideNextIcon from "../images/SlideNextIcon";
import SlidePrevIcon from "../images/SlidePrevIcon";
import { isNullorUndefined } from "../../../utils/ObjectUtils";
import { Program, ProgramDetail, RelatedVideo, Video } from "../../../stores/program/model/Program";
import ProgramDetailModal from "../modal/ProgramDetail";
import { useHistory } from "react-router-dom";

SwiperCore.use([Navigation]);

// スライドの型定義
export interface VideoSlideGroupProps {
    // id
    id: string;
    // 一列に表示させる画像数
    displayNum: number;
    //プログラムリスト（受講中プログラム表示）
    programList?: Program[] | ProgramDetail[];
    // 画像のループ切り替え
    needLoop: boolean;
    // ビデオリスト（モーダル画面非表示）
    videoListTransition: VideoListTransition[];
}

// ビデオリスト（モーダル画面非表示）型定義
export interface VideoListTransition {
    // id
    id: string;
    // ビデオタイトル
    title: string;
    // ビデオサムネイル
    imageUrl?: string;
    //ビデオ説明
    description?: string;
    //プレゼンター
    sub?: string;
    //プレゼンター
    professional?: string;
    //プレゼンターサムネイル
    professionalImageUrl?: string;
    // 再生時間
    durationMillis?: number;
    // 視聴時間
    progressMillis?: number;
    // 新着マーク
    visibleNewIcon?: boolean;
    //タグリスト
    tags?: string[];
}

const useStyles = makeStyles(() =>
    createStyles({
        barColorPrimary: {
            backgroundColor: "var(--studio-btn-bg)",
        },
    })
);

export default function VideoSlideGroup({
    id,
    displayNum,
    programList,
    needLoop,
    videoListTransition,
}: VideoSlideGroupProps) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;

    const [open, setOpen] = React.useState(false);
    const [selectedVideo, setSelectedVideo] = React.useState<Video>();
    const [videos, setVideos] = React.useState<Video[]>();
    const [related, setRelated] = React.useState<RelatedVideo[]>();
    const [title, setTitle] = React.useState("");
    const [des, setDes] = React.useState("");

    const classes = useStyles();
    const history = useHistory();

    const handleOpen = (value: any) => {
        setOpen(true);
        // セレクトビデオエリアの画像変更
        const selectId: string = value.target.id;
        // ビデオをクリック
        if (videoListTransition !== undefined && programList !== undefined) {
            for (const program of programList) {
                if (program.id === id) {
                    // パラメータをセット
                    setVideos(program.videos);
                    setRelated(program.relatedVideos);
                    setDes(program.description);
                    setTitle(program.title);
                }
                for (const video of program.videos) {
                    if (video.id === selectId) {
                        // フォーカスビデオをセット
                        setSelectedVideo(video);
                        break;
                    }
                }
            }
        }
    };

    // 視聴履歴の進捗インジケータを計算
    const normalise = (row: any) => {
        if (isNullorUndefined(row.viewingActivities) || row.viewingActivities.length === 0) {
            return 0;
        } else {
            const progressMillis = row.viewingActivities[0].progressMillis;
            const isFinished = row.viewingActivities[0].isFinished;
            if (progressMillis !== null) {
                const rate = Math.floor(
                    (Number(progressMillis) / Number(row.durationMillis)) * 100
                );
                return rate;
            } else if (progressMillis === null && isFinished) {
                return 100;
            }
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <div style={{ position: "relative" }}>
            {((IsDevice("desktop") && videoListTransition.length > 5) ||
                (IsDevice("ipad") && videoListTransition.length > 3) ||
                (IsDevice("iphone") && videoListTransition.length > 2)) && (
                <>
                    <div
                        className={`${styles.swiperButtonDiv} swiper-video-next-${id} ${styles.swiperRight} swiper-button-disabled`}
                        style={{
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "10px",
                            height: "calc(14vw - 60px)",
                            right: IsDevice("desktop")
                                ? "-30px"
                                : IsDevice("ipad")
                                ? "-19px"
                                : "-11px",
                            top: IsDevice("desktop") ? "0px" : "45px",
                            zIndex: 10,
                            cursor: "pointer",
                        }}
                    >
                        <div className={styles.swiperButton}>
                            <SlideNextIcon />
                        </div>
                    </div>
                    <div
                        className={`${styles.swiperButtonDiv} swiper-video-prev-${id} ${styles.swiperLeft} swiper-button-disabled`}
                        style={{
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "10px",
                            height: "calc(14vw - 60px)",
                            left: IsDevice("desktop")
                                ? "-30px"
                                : IsDevice("ipad")
                                ? "-19px"
                                : "-11px",
                            top: IsDevice("desktop") ? "0px" : "45px",
                            zIndex: 10,
                            cursor: "pointer",
                        }}
                    >
                        <div className={styles.swiperButton}>
                            <SlidePrevIcon />
                        </div>
                    </div>
                </>
            )}
            <Swiper
                spaceBetween={15}
                slidesPerView={displayNum}
                loop={needLoop ? (videoListTransition.length >= displayNum ? true : false) : false}
                loopFillGroupWithBlank={
                    needLoop ? (videoListTransition.length >= displayNum ? false : true) : true
                }
                navigation={{
                    nextEl: `.swiper-video-next-${id}`,
                    prevEl: `.swiper-video-prev-${id}`,
                }}
            >
                {videoListTransition.map((row, index) => (
                    <SwiperSlide key={`${id}-${index}`}>
                        {/* <div className={styles.hoverContentCard}>
                            <img
                                id={row.id}
                                src={row.imageUrl}
                                className={styles.hoverSlideImg}
                                onError={(e) =>
                                    (e.currentTarget.src = require("../../../assets/image/NoImage.jpg"))
                                }
                                alt={row.id}
                                onClick={
                                    programList === undefined
                                        ? () => history.push("/video/" + row.id)
                                        : handleOpen
                                }
                            />
                            <div className={styles.hoverSub}>
                                <div
                                    id={row.id}
                                    data-tip={row.title}
                                    data-for="record"
                                    className={styles.hoverSlideTitle}
                                    onClick={
                                        programList === undefined
                                            ? () =>
                                                  history.push({
                                                      pathname: "./video/" + row.id,
                                                      state: { time: row.progressMillis },
                                                  })
                                            : handleOpen
                                    }
                                >
                                    {row.title}
                                </div>
                                <div className={styles.presenter}>
                                    <img
                                        src={row.professionalImageUrl}
                                        className={styles.presenterIcon}
                                        alt={row.id}
                                    />
                                    {row.professional}
                                </div>
                                <div
                                    id={row.id}
                                    data-tip={row.description}
                                    className={styles.hoverDescription}
                                >
                                    {row.description || row.sub}
                                </div>
                                {row.tags && row.tags.length > 0 && (
                                    <div className={styles.tagArea}>
                                        <div className={styles.tagList}>
                                            {row.tags.map((tag) => (
                                                <div key={tag} className={styles.tag}>
                                                    {tag}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div> */}
                        <div className={styles.slideDiv}>
                            <img
                                id={row.id}
                                src={row.imageUrl}
                                className={styles.slideImg}
                                onError={(e) =>
                                    (e.currentTarget.src = require("../../../assets/image/NoImage.jpg"))
                                }
                                alt={row.id}
                                onClick={
                                    programList === undefined
                                        ? () => history.push("/video/" + row.id)
                                        : handleOpen
                                }
                            />
                            {row.durationMillis !== undefined && row.durationMillis > 0 && (
                                <LinearProgress
                                    className={styles.progressBar}
                                    style={{ height: "3px" }}
                                    classes={{ barColorPrimary: classes.barColorPrimary }}
                                    variant="determinate"
                                    value={normalise(row)}
                                />
                            )}
                        </div>
                        <div
                            id={row.id}
                            data-tip={row.title}
                            data-for="record"
                            className={styles.slideTitle}
                            onClick={
                                programList === undefined
                                    ? () =>
                                          history.push({
                                              pathname: "./video/" + row.id,
                                              state: { time: row.progressMillis },
                                          })
                                    : handleOpen
                            }
                        >
                            {row.title}
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
            <Modal
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
                className={styles.modal}
                open={open}
                onClose={handleClose}
                closeAfterTransition
            >
                <ProgramDetailModal
                    in={open}
                    onClose={handleClose}
                    title={title}
                    description={des}
                    episodelist={videos}
                    selected={selectedVideo}
                    related={related}
                />
            </Modal>
            {/* Tooltip */}
            <PuiTooltip id={"record"} />
        </div>
    );
}
